import React from 'react';
import { Helmet } from 'react-helmet-async';

import { setMapboxAccessToken } from './mapboxLoader';

const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
const MAPBOX_SDK_SCRIPT_ID = 'mapbox_SDK';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';

/**
 * Include scripts (like Map Provider).
 * These scripts are relevant for whole application: location search in Topbar and maps on different pages.
 * However, if you don't need location search and maps, you can just omit this component from app.js
 * Note: another common point to add <scripts>, <links> and <meta> tags is Page.js
 *       and Stripe script is added in public/index.html
 *
 * Note 2: When adding new external scripts/styles/fonts/etc.,
 *         if a Content Security Policy (CSP) is turned on, the new URLs
 *         should be whitelisted in the policy. Check: server/csp.js
 */
export const IncludeScripts = props => {
  const { marketplaceRootURL: rootURL, maps, analytics } = props?.config || {};
  const { googleAnalyticsId, plausibleDomains } = analytics;

  const { mapProvider, googleMapsAPIKey, mapboxAccessToken } = maps || {};
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  const isMapboxInUse = mapProvider === 'mapbox';

  // Add Google Analytics script if correct id exists (it should start with 'G-' prefix)
  // See: https://developers.google.com/analytics/devguides/collection/gtagjs
  const hasGoogleAnalyticsv4Id = googleAnalyticsId?.indexOf('G-') === 0;

  // Collect relevant map libraries
  let mapLibraries = [];
  let analyticsLibraries = [];

  if (isMapboxInUse) {
    // NOTE: remember to update mapbox-sdk.min.js to a new version regularly.
    // mapbox-sdk.min.js is included from static folder for CSP purposes.
    // async: this library is heavy (WebGL map engine) and is only actually needed for
    // location search / map display, not for the initial render of any page, so it
    // shouldn't be allowed to block HTML parsing or the main thread on page load.
    mapLibraries.push(
      <script
        id={MAPBOX_SDK_SCRIPT_ID}
        key="mapboxSDK"
        async
        src={`${rootURL}/static/scripts/mapbox/mapbox-sdk.min.js`}
      ></script>
    );
    // NOTE: Mapbox GL's stylesheet (mapbox-gl.css) is intentionally NOT included
    // here. Loading it globally made it a render-blocking request on every page
    // (including map-less pages like the landing page), hurting FCP/LCP. It is
    // now injected on demand — only when an interactive map actually mounts — via
    // ensureMapboxCss() in util/mapboxLoader.js (called from the map components).
    // Add Mapbox library
    mapLibraries.push(
      <script
        id={MAPBOX_SCRIPT_ID}
        key="mapbox_GL_JS"
        async
        src="https://api.mapbox.com/mapbox-gl-js/v1.0.0/mapbox-gl.js"
        crossOrigin
      ></script>
    );
  } else if (isGoogleMapsInUse) {
    // Add Google Maps library (async for the same reason as Mapbox above)
    mapLibraries.push(
      <script
        id={GOOGLE_MAPS_SCRIPT_ID}
        key="GoogleMapsApi"
        async
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
        crossOrigin
      ></script>
    );
  }

  if (googleAnalyticsId && hasGoogleAnalyticsv4Id) {
    // Google Analytics: gtag.js
    // NOTE: This template is a single-page application (SPA).
    //       gtag.js sends initial page_view event after page load.
    //       but we need to handle subsequent events for in-app navigation.
    //       This is done in src/analytics/handlers.js
    analyticsLibraries.push(
      <script
        key="gtag.js"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
        crossOrigin
      ></script>
    );

    analyticsLibraries.push(
      <script key="gtag dataLayer">
        {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
      
        gtag('config', '${googleAnalyticsId}', {
          cookie_flags: 'SameSite=None;Secure',
        });
        `}
      </script>
    );
  }

  if (plausibleDomains) {
    // If plausibleDomains is not an empty string, include their script too.
    analyticsLibraries.push(
      <script
        key="plausible"
        defer
        src="https://plausible.io/js/script.js"
        data-domain={plausibleDomains}
        crossOrigin
      ></script>
    );
  }

  // Hand the access token over to the loader. It applies the token as soon as
  // window.mapboxgl exists, whether that is already the case now or only later
  // once the async script has executed. Going through the loader instead of
  // assigning here directly matters because with `async` scripts the library is
  // usually NOT available yet at hydration time, and there is no guarantee that
  // this component re-renders after it finally loads.
  if (isMapboxInUse) {
    setMapboxAccessToken(mapboxAccessToken);
  }

  // Fast path: if the script is added on client side as a reaction to page
  // navigation or the app is rendered on client side entirely (e.g.
  // HMR/WebpackDevServer), apply the token the moment it loads rather than
  // waiting for the loader's next poll.
  const onMapLibLoaded = () => {
    if (isMapboxInUse) {
      setMapboxAccessToken(mapboxAccessToken);
    }
  };

  // React Helmet Async doesn't support onLoad prop for scripts.
  // However, it does have onChangeClientState functionality.
  // We can use that to start listen 'load' events when the library is added on client-side.
  const onChangeClientState = (newState, addedTags) => {
    if (addedTags && addedTags.scriptTags) {
      const foundScripts = addedTags.scriptTags.filter(s =>
        [MAPBOX_SCRIPT_ID, MAPBOX_SDK_SCRIPT_ID, GOOGLE_MAPS_SCRIPT_ID].includes(s.id)
      );
      foundScripts.forEach(s => {
        s.addEventListener('load', onMapLibLoaded, { once: true });
      });
    }
  };

  const allScripts = [...analyticsLibraries, ...mapLibraries];
  return <Helmet onChangeClientState={onChangeClientState}>{allScripts}</Helmet>;
};
